import React from 'react';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useCricketFormat } from '@/hooks/useUnitPreferences';

interface SpeedGaugeProps {
  value: number;
  optimal?: number;
  max: number;
  unit: string;
  color: string;
  label: string;
  showImprovement?: boolean;
  improvement?: number;
}

export const SpeedGauge: React.FC<SpeedGaugeProps> = ({
  value,
  optimal,
  max,
  unit,
  color,
  label,
  showImprovement = false,
  improvement = 0
}) => {
  const { formatSpeed, preferences } = useCricketFormat();
  
  // Fallback function if formatting fails
  const safeFormatSpeed = (val: number, includeUnit: boolean = true) => {
    try {
      return formatSpeed(val, includeUnit);
    } catch (error) {
      console.warn('Unit formatting error:', error);
      return includeUnit ? `${val.toFixed(1)} m/s` : val.toFixed(1);
    }
  };
  const percentage = (value / max) * 100;
  const optimalPercentage = optimal ? (optimal / max) * 100 : undefined;
  
  const getPerformanceColor = () => {
    if (!optimal) return color;
    const ratio = value / optimal;
    if (ratio >= 0.95) return '#10b981'; // green
    if (ratio >= 0.85) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  return (
    <div className="space-y-4">
      {/* Main Speed Display */}
      <div className="text-center">
        <div className="text-4xl font-bold" style={{ color: getPerformanceColor() }}>
          {safeFormatSpeed(value, false)}
        </div>
        <p className="text-sm text-gray-600">{preferences?.speed || unit}</p>
        <p className="text-xs text-gray-500 mt-1">{label}</p>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-gray-600">
          <span>0</span>
          <span>{safeFormatSpeed(max)}</span>
        </div>
        
        <div className="relative">
          <Progress 
            value={percentage} 
            className="h-3"
            style={{ 
              backgroundColor: '#e5e7eb',
            }}
          />
          
          {/* Optimal marker */}
          {optimal && (
            <div 
              className="absolute top-0 h-3 w-1 bg-green-600 rounded"
              style={{ left: `${optimalPercentage}%` }}
              title={`Optimal: ${safeFormatSpeed(optimal)}`}
            />
          )}
        </div>
      </div>

      {/* Comparison with Optimal */}
      {optimal && (
        <div className="bg-gray-50 p-3 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Actual:</span>
            <span className="font-medium">{safeFormatSpeed(value)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Optimal:</span>
            <span className="font-medium text-green-600">{safeFormatSpeed(optimal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Difference:</span>
            <span className={`font-medium ${(value - optimal) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(value - optimal) >= 0 ? '+' : ''}{safeFormatSpeed(Math.abs(value - optimal))}
            </span>
          </div>
        </div>
      )}

      {/* Improvement Indicator */}
      {showImprovement && improvement !== 0 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          {improvement > 0 ? (
            <TrendingUp className="h-4 w-4 text-green-600" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-600" />
          )}
          <span className={improvement > 0 ? 'text-green-600' : 'text-red-600'}>
            {improvement > 0 ? '+' : ''}{safeFormatSpeed(Math.abs(improvement))} potential
          </span>
        </div>
      )}

      {/* Performance Rating */}
      {optimal && (
        <div className="text-center">
          <div className="text-xs text-gray-500">Performance Rating</div>
          <div className="flex justify-center mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`h-2 w-6 mx-0.5 rounded ${
                  i < Math.floor((value / optimal) * 5) 
                    ? 'bg-current' 
                    : 'bg-gray-200'
                }`}
                style={{ 
                  color: getPerformanceColor()
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};